/**
 * CreateLifecycleActionButton Component
 *
 * Renders a lifecycle action button for CREATE mode that performs:
 * 1. Form validation
 * 2. Record creation (POST)
 * 3. Lifecycle action (POST to /{uid}/{action})
 *
 * This provides "Create & Activate" or "Create & Submit" functionality
 * depending on the approval workflow configuration.
 */

import { useState } from 'react'
import { App, Button, Popconfirm, Tooltip } from 'antd'
import * as Icons from '@ant-design/icons'
import { apiService } from '@/services/apiService'
import type { LifecycleAction } from '@/services/metadataService'
import { SplicingPreviewModal } from '@/pages/modules/components/SplicingPreviewModal'
import type {
  SplicingPreviewData,
  SplicingText,
  ActivateRequest,
} from '@/pages/modules/data/types'

/**
 * Props for CreateLifecycleActionButton
 */
export interface CreateLifecycleActionButtonProps {
  /** The action key (e.g., 'activate', 'submit') */
  actionKey: string
  /** Action metadata from backend */
  action: LifecycleAction
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Entity label for messages (e.g., 'Offering') */
  entityLabel: string
  /** Key field name (default: 'uid') */
  keyField?: string
  /** Function to validate and get form values */
  getFormValues: () => Promise<Record<string, unknown> | null>
  /** Called after successful create + action */
  onSuccess?: (record: Record<string, unknown>) => void
  /** Whether the form is currently submitting */
  disabled?: boolean
}

/**
 * Interpolate placeholders in message templates
 */
function interpolateMessage(
  template: string,
  entityLabel: string,
  record: Record<string, unknown>
): string {
  return template
    .replace(/\{entity\}/g, entityLabel)
    .replace(/\{name\}/g, String(record.name || record.uid || ''))
    .replace(/\{uid\}/g, String(record.uid || ''))
}

/**
 * Get Ant Design icon component by name
 */
function getIconComponent(iconName?: string): React.ReactNode {
  if (!iconName) return null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IconComponent = (Icons as any)[iconName] as React.ComponentType | undefined
  if (!IconComponent) {
    console.warn(`[CreateLifecycleActionButton] Unknown icon: ${iconName}`)
    return null
  }
  return <IconComponent />
}

/**
 * CreateLifecycleActionButton Component
 *
 * Provides "Create & [Action]" functionality for lifecycle-enabled entities.
 */
export function CreateLifecycleActionButton({
  actionKey,
  action,
  endpoint,
  entityLabel,
  keyField = 'uid',
  getFormValues,
  onSuccess,
  disabled = false,
}: CreateLifecycleActionButtonProps) {
  const { message, modal } = App.useApp()
  const [loading, setLoading] = useState(false)

  // Splicing preview state (for activate action)
  const [showSplicingPreview, setShowSplicingPreview] = useState(false)
  const [splicingPreviewData, setSplicingPreviewData] = useState<SplicingPreviewData | null>(null)
  const [splicingText, setSplicingText] = useState<SplicingText | null>(null)
  const [createdRecord, setCreatedRecord] = useState<Record<string, unknown> | null>(null)

  /**
   * Create the record via POST
   */
  const createRecord = async (payload: Record<string, unknown>): Promise<Record<string, unknown> | null> => {
    const response = await apiService.post<Record<string, unknown>>(endpoint, payload)
    if (response.success && response.data) {
      return response.data
    }
    return null
  }

  /**
   * Fetch splicing preview for activate action on newly created record
   */
  const fetchSplicingPreview = async (record: Record<string, unknown>): Promise<boolean> => {
    const uid = record[keyField] as string
    if (!uid) return false

    try {
      const request: ActivateRequest = {
        preview: true,
        createContinuations: true,
      }

      const response = await apiService.post<SplicingPreviewData>(
        `${endpoint}/${uid}/${actionKey}`,
        request
      )

      if (response.success && response.data) {
        const data = response.data
        const meta = response.meta as { splicingText?: SplicingText; splicingPreviewable?: boolean } | undefined

        // Only show preview if there are timeline impacts
        if (
          meta?.splicingPreviewable &&
          (data.supersessions?.length > 0 || data.continuations?.length > 0)
        ) {
          setSplicingPreviewData(data)
          setSplicingText(meta.splicingText || null)
          setCreatedRecord(record)
          setShowSplicingPreview(true)
          return true
        }
      }

      return false
    } catch (error) {
      console.error(`[CreateLifecycleActionButton] Preview fetch error:`, error)
      return false
    }
  }

  /**
   * Execute the lifecycle action on created record
   */
  const executeLifecycleAction = async (
    record: Record<string, unknown>,
    createContinuations?: boolean
  ): Promise<boolean> => {
    const uid = record[keyField] as string
    if (!uid) {
      message.error('Created record has no UID')
      return false
    }

    const request: Record<string, unknown> = {}
    if (actionKey === 'activate' && createContinuations !== undefined) {
      request.preview = false
      request.createContinuations = createContinuations
    }

    const response = await apiService.post(`${endpoint}/${uid}/${actionKey}`, request)

    if (response.success) {
      const successMsg = action.successMessage
        ? interpolateMessage(action.successMessage, entityLabel, record)
        : `${entityLabel} created and ${actionKey}d successfully`
      message.success(successMsg)
      return true
    }

    console.error(`[CreateLifecycleActionButton] ${actionKey} failed:`, response.errors)
    return false
  }

  /**
   * Show simple confirmation dialog for activate action when no splicing preview is needed
   */
  const showSimpleActivateConfirm = (record: Record<string, unknown>) => {
    const recordName = record.name || record[keyField] || 'this record'
    modal.confirm({
      title: `Activate ${entityLabel}`,
      content: `Are you sure you want to activate "${recordName}"? This action cannot be undone.`,
      okText: 'Activate',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoading(true)
        try {
          const success = await executeLifecycleAction(record, false)
          if (success) {
            onSuccess?.(record)
          }
        } finally {
          setLoading(false)
        }
      },
    })
  }

  /**
   * Handle the full create + action flow
   */
  const handleAction = async () => {
    // Get validated form values
    const payload = await getFormValues()
    if (!payload) {
      // Form validation failed
      return
    }

    setLoading(true)
    try {
      // Step 1: Create the record
      const record = await createRecord(payload)
      if (!record) {
        // Create failed (error already shown by apiService)
        return
      }

      // Step 2: For activate action, check for splicing preview
      if (actionKey === 'activate') {
        const showedPreview = await fetchSplicingPreview(record)
        if (showedPreview) {
          // Preview modal is now showing, wait for user confirmation
          // Keep loading false so user can interact with modal
          setLoading(false)
          return
        }
        // No conflicts or preview not available - show simple confirmation dialog
        setLoading(false)
        showSimpleActivateConfirm(record)
        return
      }

      // Step 3: Execute lifecycle action (non-activate actions)
      const success = await executeLifecycleAction(record)
      if (success) {
        onSuccess?.(record)
      }
    } catch (error) {
      console.error(`[CreateLifecycleActionButton] Error:`, error)
      message.error(`Failed to create and ${actionKey} ${entityLabel.toLowerCase()}`)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle splicing preview confirmation
   */
  const handleSplicingConfirm = async (createContinuations: boolean) => {
    if (!createdRecord) return

    setLoading(true)
    try {
      const success = await executeLifecycleAction(createdRecord, createContinuations)
      if (success) {
        onSuccess?.(createdRecord)
      }
    } finally {
      setLoading(false)
      setShowSplicingPreview(false)
      setSplicingPreviewData(null)
      setSplicingText(null)
      setCreatedRecord(null)
    }
  }

  /**
   * Handle splicing preview cancellation
   * Note: Record was already created, so we just close the modal
   * The record remains as a draft
   */
  const handleSplicingCancel = () => {
    setShowSplicingPreview(false)
    setSplicingPreviewData(null)
    setSplicingText(null)
    // Record is created but not activated - notify parent to refresh
    if (createdRecord) {
      message.info(`${entityLabel} created as draft. You can activate it later.`)
      onSuccess?.(createdRecord)
    }
    setCreatedRecord(null)
  }

  // Build button label: "Create & [Action]"
  const buttonLabel = `Create & ${action.label}`

  // Splicing preview modal
  const splicingModal = (
    <SplicingPreviewModal
      visible={showSplicingPreview}
      previewData={splicingPreviewData}
      splicingText={splicingText}
      loading={loading}
      entityLabel={entityLabel}
      onConfirm={handleSplicingConfirm}
      onCancel={handleSplicingCancel}
    />
  )

  // For activate actions, skip Popconfirm - splicing preview modal handles confirmation
  if (actionKey === 'activate') {
    return (
      <>
        <Tooltip title={buttonLabel} destroyOnHidden>
          <Button
            type={action.buttonType || 'default'}
            danger={action.danger}
            icon={getIconComponent(action.icon)}
            onClick={handleAction}
            loading={loading}
            disabled={disabled}
            style={{
              minWidth: 140,
              height: 36,
            }}
          >
            {buttonLabel}
          </Button>
        </Tooltip>
        {splicingModal}
      </>
    )
  }

  // For actions with confirmation message, wrap in Popconfirm
  if (action.confirmationMessage) {
    const confirmMsg = `Create ${entityLabel.toLowerCase()} and ${action.label.toLowerCase()}?`

    return (
      <>
        <Popconfirm
          title={confirmMsg}
          onConfirm={handleAction}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: action.danger }}
          disabled={disabled}
        >
          <Button
            type={action.buttonType || 'default'}
            danger={action.danger}
            icon={getIconComponent(action.icon)}
            loading={loading}
            disabled={disabled}
            style={{
              minWidth: 140,
              height: 36,
            }}
          >
            {buttonLabel}
          </Button>
        </Popconfirm>
        {splicingModal}
      </>
    )
  }

  // No confirmation - direct action
  return (
    <>
      <Button
        type={action.buttonType || 'default'}
        danger={action.danger}
        icon={getIconComponent(action.icon)}
        onClick={handleAction}
        loading={loading}
        disabled={disabled}
        style={{
          minWidth: 140,
          height: 36,
        }}
      >
        {buttonLabel}
      </Button>
      {splicingModal}
    </>
  )
}
